import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { map } from "ramda"
import { flatten } from "lodash"
import { pipeline } from "@figurepos/fgr-lib-business-logic/lib/common/functional"
import type { StateObject } from "../redux/types"
import type { AuthState } from "./reducer"
import type { HttpHeaders } from "../../api/types"
import { getUserAccountById } from "../data/selectors"
import type { LocationAuthData, TerminalAuthData } from "./types"
import type { UserAccount } from "../data/types"
import { AggregatorType, AuthAccount2, TerminalType } from "../../graphql/types"
import { OrganizationData } from "./types"
import { getCookieAccountId, getCookieOrganizationId, getCookieSession } from "./cookies"
import { getApiKey } from "../../utils/apiKeys"
import { createSelector } from "reselect"

export const getAuthStateObject = (state: StateObject): AuthState => state.auth

export const getSession = (state: StateObject): Maybe<string> => getAuthStateObject(state).session

export const getAuthUserId = (state: StateObject): Maybe<string> => getAuthStateObject(state).userId

export const getAuthUserEmail = (state: StateObject): Maybe<string> => getAuthStateObject(state).userEmail

export const getUserRole = (state: StateObject): Maybe<string> => getAuthStateObject(state).role

// This is temporary fix for allowing organization users to have access to all account permissions
export const hasOrganizationAccess = (state: StateObject): boolean => {
    const organizations = getOrganizations(state)
    const account = getSelectedAccount(state)
    if (!account || !organizations || organizations.length === 0) {
        return false
    }
    return organizations.some(o => o.id === account.organizationId)
}

export const getIsSuperUser = (state: StateObject): boolean => getUserRole(state) === "superuser"

export const hasMPOSEnabled = (state: StateObject): boolean => getIsSuperUser(state)

export const hasMasterListAccess = (state: StateObject): boolean =>
    getIsSuperUser(state) || getAuthStateObject(state).masterListAccess || hasOrganizationAccess(state)

export const getAccounts = (state: StateObject): Maybe<AuthAccount2[]> => getAuthStateObject(state).accounts

export const getOrganizations = (state: StateObject): Maybe<OrganizationData[]> =>
    getAuthStateObject(state).organizations

export const getSelectedOrganization = (state: StateObject): Maybe<OrganizationData> =>
    (getOrganizations(state) ?? []).find(o => o.id === getSelectedOrganizationId(state))

export const getLocationsAuthData = (state: StateObject): LocationAuthData[] =>
    getAuthStateObject(state).locations || []

export const getLocationsAuthDataCount = (state: StateObject): number => getLocationsAuthData(state).length

export const getLocationAuthDataById = (state: StateObject, id: string): Maybe<LocationAuthData> =>
    getLocationsAuthData(state).find(l => l.id === id)

export const getLocationDataByTerminalId = (state: StateObject, terminalId): Maybe<LocationAuthData> =>
    getLocationsAuthData(state).find(l => l.terminals.some(t => t.id === terminalId))

export const areLocationsAuthDataLoaded = (state: StateObject): boolean => getAuthStateObject(state).locations != null

export const getSelectedAccountId = (state: StateObject): Maybe<string> => getAuthStateObject(state).selectedAccountId

export const getSelectedOrganizationId = (state: StateObject): Maybe<string> =>
    getAuthStateObject(state).selectedOrganizationId

export const getTerminalsAuthData = (state: StateObject): TerminalAuthData[] =>
    pipeline(
        getLocationsAuthData(state) || [],
        map((l: LocationAuthData) => l.terminals || []),
        flatten
    )

export const getTerminalAuthDataById = (state: StateObject, id: string): Maybe<TerminalAuthData> =>
    getTerminalsAuthData(state).find(t => t.id === id)

export const shouldShowLocationsPicker = (state: StateObject): boolean => getLocationsAuthData(state).length > 1

export const getSelectedAccount = (state: StateObject): Maybe<AuthAccount2> => {
    const id = getSelectedAccountId(state)
    const accounts = getAccounts(state)
    return id && accounts ? accounts.find(a => a.id === id) : null
}

export const getSelectedAccountName = (state: StateObject): Maybe<string> => {
    const account = getSelectedAccount(state)
    return account ? account.name : null
}

export const getSignedInUserAccount = (state: StateObject): Maybe<UserAccount> => {
    const account = getSelectedAccount(state)

    if (!account) {
        return null
    }

    return getUserAccountById(state, account.userAccountId)
}

export const getSignInUserFirstName = (state: StateObject): Maybe<string> => {
    const account = getSelectedAccount(state)

    if (!account) {
        return null
    }

    const accountData = getUserAccountById(state, account.userAccountId)
    return accountData ? accountData.firstName : null
}

export const getSignInUserSurname = (state: StateObject): Maybe<string> => {
    const account = getSelectedAccount(state)

    if (!account) {
        return null
    }

    const accountData = getUserAccountById(state, account.userAccountId)
    return accountData ? accountData.surname : null
}

export const getUserAccountId = (state: StateObject): Maybe<string> => {
    const account = getSelectedAccount(state)
    return account ? account.userAccountId : null
}

export const getUserAccountName = (state: StateObject): Maybe<string> => {
    const account = getSelectedAccount(state)
    return account ? account.name : null
}

export const getHttpHeaders = createSelector(
    [getSelectedAccountId, getSelectedOrganizationId, getSession],
    (accountId, organizationId, session): HttpHeaders => {
        const resolvedAccountId = accountId ?? getCookieAccountId()
        const resolvedOrganizationId = organizationId ?? getCookieOrganizationId()
        const resolvedSession = session ?? getCookieSession()

        const result: HttpHeaders = {
            "twbrds-api-version": "2.0.5",
            "figure-api-key": getApiKey(),
        }
        if (resolvedSession) {
            result["twbrds-session"] = resolvedSession
        }
        if (resolvedAccountId) {
            result["twbrds-account-id"] = resolvedAccountId
        }
        if (resolvedOrganizationId) {
            result["twbrds-organization-id"] = resolvedOrganizationId
        }
        return result
    }
)

export const getTerminalsAuthDataByType = (state: StateObject, type: TerminalType): TerminalAuthData[] =>
    getTerminalsAuthData(state).filter(t => {
        return t.type === type
    })

export const getMiBTerminalIdsByLocationId = (state: StateObject, locationId: Maybe<string>): string[] =>
    getTerminalsAuthData(state)
        .filter(t => {
            return t.type === TerminalType.Mib && t.locationId === locationId
        })
        .map((t: TerminalAuthData) => t.id)

export const getMakeItButterTerminalIds = (state: StateObject): string[] => {
    const ids = getTerminalsAuthDataByType(state, TerminalType.Mib).map((t: TerminalAuthData) => t.id)
    const aggregatorTerminalIds = getTerminalsAuthDataByType(state, TerminalType.Aggregator).map(
        (t: TerminalAuthData) => t.id
    )
    return [...ids, ...aggregatorTerminalIds]
}

export const isTerminalOfAggregatorType = (state: StateObject, id: string, aggregatorType: AggregatorType): boolean => {
    const t = getTerminalAuthDataById(state, id)
    if (!t) {
        return false
    }
    if (t.type !== TerminalType.Aggregator) {
        return false
    }
    return t.aggregatorType === aggregatorType
}

export const canSyncMenu = (state: StateObject, id: string): boolean => {
    const t = getTerminalAuthDataById(state, id)
    if (!t) {
        return false
    }
    if (![AggregatorType.Grubhub, AggregatorType.Uber].includes(t.aggregatorType)) {
        return false
    }
    return true
}
